import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { 
  ArrowRight, 
  ChevronDown, 
  Shield, 
  Wrench, 
  FileCheck, 
  Clock, 
  Award,
  CheckCircle2,
  Star,
  ArrowUpRight,
  Play,
  Users,
  Building,
  HardHat
} from 'lucide-react';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

// Counter Animation Component
const AnimatedCounter = ({ end, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Section Component with animation
const Section = ({ children, className = '', id }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const services = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Linee Vita",
      description: "Sistemi di ancoraggio permanenti tipo C conformi UNI EN 795 per la sicurezza in copertura.",
      link: "/servizi#linee-vita",
      featured: true
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Parapetti",
      description: "Parapetti anticaduta in alluminio con fissaggio modulare e installazione rapida.",
      link: "/servizi#parapetti",
      featured: false
    },
    {
      icon: <FileCheck className="w-8 h-8" />,
      title: "Progettazione",
      description: "Analisi e progettazione personalizzata dei sistemi anticaduta per ogni tipologia di copertura.",
      link: "/servizi#progettazione",
      featured: false
    },
    {
      icon: <HardHat className="w-8 h-8" />,
      title: "Installazione",
      description: "Installazione certificata da tecnici qualificati secondo le normative vigenti.",
      link: "/servizi#installazione",
      featured: false
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Revisione Periodica",
      description: "Controlli e manutenzione periodica obbligatoria secondo D.Lgs. 81/08 e UNI 11560.",
      link: "/servizi#revisione",
      featured: true
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Certificazioni",
      description: "Rilascio documentazione tecnica e certificazioni di conformità per ogni intervento.",
      link: "/normative",
      featured: false
    }
  ];

  const process = [
    {
      step: "01",
      title: "Sopralluogo",
      description: "Analisi gratuita della copertura e valutazione dei requisiti di sicurezza."
    },
    {
      step: "02",
      title: "Progettazione",
      description: "Studio personalizzato e definizione del sistema anticaduta ottimale."
    },
    {
      step: "03",
      title: "Installazione",
      description: "Montaggio professionale certificato con documentazione completa."
    },
    {
      step: "04",
      title: "Certificazione",
      description: "Rilascio di certificati di conformità e piano di manutenzione."
    }
  ];

  const testimonials = [
    {
      quote: "Professionali e competenti. Hanno installato le linee vita sul nostro capannone in tempi record.",
      author: "Marco B.",
      role: "Direttore Tecnico",
      company: "Industrie Metalliche Verona"
    },
    {
      quote: "Finalmente un partner affidabile per la sicurezza. Le revisioni periodiche sono puntuali e precise.",
      author: "Elena R.",
      role: "Amministratore",
      company: "Condominio Residence Garda"
    },
    {
      quote: "La progettazione personalizzata ha risolto tutte le criticità del nostro tetto storico.",
      author: "Arch. Paolo M.",
      role: "Progettista",
      company: "Studio Architettura M&P"
    }
  ];

  const faqs = [
    {
      question: "Quando è obbligatoria l'installazione di linee vita?",
      answer: "Le linee vita sono obbligatorie su tutte le coperture dove si prevede l'accesso per manutenzione, secondo il D.Lgs. 81/08. Molte Regioni hanno emanato normative specifiche che rendono obbligatoria l'installazione anche per nuove costruzioni e ristrutturazioni."
    },
    {
      question: "Ogni quanto va fatta la revisione delle linee vita?",
      answer: "La norma UNI 11560 prevede una revisione periodica almeno annuale da parte di personale qualificato. Inoltre, è necessaria un'ispezione visiva prima di ogni utilizzo e una revisione straordinaria dopo eventi anomali."
    },
    {
      question: "Quali certificazioni rilasciate?",
      answer: "Rilasciamo certificazione di conformità UNI EN 795, dichiarazione di corretta installazione, relazione tecnica con schema di ancoraggio, e libretto di manutenzione con piano delle revisioni periodiche."
    },
    {
      question: "Quanto tempo richiede un'installazione?",
      answer: "I tempi variano in base alla complessità del progetto. Una linea vita standard su copertura piana richiede generalmente 1-2 giorni. Sistemi più complessi su coperture irregolari possono richiedere 3-5 giorni."
    },
    {
      question: "Intervenite su edifici storici?",
      answer: "Sì, abbiamo esperienza specifica su edifici vincolati e storici. Utilizziamo sistemi a basso impatto visivo e soluzioni compatibili con i vincoli architettonici, sempre in coordinamento con le Soprintendenze."
    }
  ];

  const stats = [
    { value: 500, suffix: '+', label: 'Progetti Completati' },
    { value: 30, suffix: '+', label: 'Anni di Esperienza' },
    { value: 100, suffix: '%', label: 'Clienti Soddisfatti' },
    { value: 24, suffix: 'h', label: 'Assistenza' }
  ];

  return (
    <div className="bg-revo-bg">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <motion.div 
          style={{ y: heroY }}
          className="absolute inset-0 z-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_lifeline-solutions/artifacts/5az7lvks_linee-vita-installazione-ottimale.jpg')`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-revo-bg/70 via-revo-bg/80 to-revo-bg" />
          <div className="absolute inset-0 bg-gradient-to-r from-revo-bg via-transparent to-revo-bg/50" />
        </motion.div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 grid-pattern opacity-30 z-10" />

        {/* Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-32 pb-20"
        >
          <div className="max-w-4xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-revo-paper/50 backdrop-blur-sm border border-revo-border px-4 py-2 mb-8"
            >
              <span className="w-2 h-2 bg-revo-orange rounded-full animate-pulse" />
              <span className="font-manrope text-sm text-gray-300 uppercase tracking-wider">
                Sicurezza Certificata UNI EN 795
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-barlow text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter leading-none mb-6"
            >
              <span className="text-white">Sicurezza</span>
              <br />
              <span className="text-revo-orange">In Quota</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-manrope text-lg md:text-xl text-gray-300 font-light leading-relaxed max-w-2xl mb-10"
            >
              Progettazione, installazione e revisione di linee vita e sistemi anticaduta. 
              Proteggiamo chi lavora in altezza con soluzioni certificate e conformi alle normative.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contatti"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 bg-revo-orange text-white px-8 py-4 font-barlow font-bold uppercase tracking-widest hover:bg-revo-orange-dark transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,102,0,0.4)]"
              >
                Richiedi Preventivo
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/servizi"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center justify-center gap-3 bg-transparent border border-revo-border text-white px-8 py-4 font-barlow font-bold uppercase tracking-widest hover:border-revo-orange hover:text-revo-orange transition-all duration-300"
              >
                Scopri i Servizi
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </motion.div>
          </div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
            data-testid="stats-section"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center md:text-left">
                <div className="font-barlow text-4xl md:text-5xl font-bold text-white">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="font-manrope text-sm text-gray-500 uppercase tracking-wider mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="flex flex-col items-center gap-2 text-gray-500"
          >
            <span className="font-manrope text-xs uppercase tracking-widest">Scopri di più</span>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </section>

      {/* Services Section - Bento Grid */}
      <Section className="py-24 md:py-32 bg-revo-bg" id="servizi">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Section Header */}
          <div className="mb-16">
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              I Nostri Servizi
            </span>
            <h2 className="font-barlow text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mt-4">
              Soluzioni Complete<br />
              <span className="text-gray-500">Per La Sicurezza</span>
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                data-testid={`service-card-${index}`}
                className={`group relative bg-revo-paper border border-revo-border p-8 hover:border-revo-orange/50 transition-all duration-500 overflow-hidden ${
                  service.featured ? 'lg:col-span-2 lg:row-span-1' : ''
                }`}
              >
                {/* Icon */}
                <div className="relative z-10 w-16 h-16 bg-revo-subtle border border-revo-border flex items-center justify-center text-revo-orange mb-6 group-hover:bg-revo-orange group-hover:text-white transition-all duration-300">
                  {service.icon}
                </div>

                {/* Content */}
                <h3 className="relative z-10 font-barlow text-2xl font-bold uppercase tracking-wide text-white mb-3 group-hover:text-revo-orange transition-colors">
                  {service.title}
                </h3>
                <p className="relative z-10 font-manrope text-gray-400 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Arrow */}
                <div className="relative z-10 flex items-center gap-2 text-gray-500 group-hover:text-revo-orange transition-colors">
                  <span className="font-manrope text-sm uppercase tracking-wider">Scopri</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                </div>

                {/* Hover Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-revo-orange/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* Process Section */}
      <Section className="py-24 md:py-32 bg-revo-paper" id="processo">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Section Header */}
          <div className="text-center mb-20">
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              Come Lavoriamo
            </span>
            <h2 className="font-barlow text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mt-4">
              Il Nostro Processo
            </h2>
          </div>

          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="process-steps">
            {process.map((item, index) => (
              <div 
                key={index}
                className="relative group"
                data-testid={`process-step-${index}`}
              >
                {/* Step Number */}
                <div className="font-barlow text-7xl font-black text-revo-subtle group-hover:text-revo-orange/20 transition-colors duration-500">
                  {item.step}
                </div>
                
                {/* Content */}
                <div className="mt-4">
                  <h3 className="font-barlow text-xl font-bold uppercase tracking-wide text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="font-manrope text-gray-400 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>

                {/* Connector Line */}
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-px bg-gradient-to-r from-revo-border to-transparent" />
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link
              to="/contatti"
              data-testid="process-cta"
              className="inline-flex items-center gap-3 bg-revo-orange text-white px-8 py-4 font-barlow font-bold uppercase tracking-widest hover:bg-revo-orange-dark transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,102,0,0.4)]"
            >
              Inizia il Tuo Progetto
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Testimonials Section */}
      <Section className="py-24 md:py-32 bg-revo-bg overflow-hidden" id="testimonials">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Section Header */}
          <div className="mb-16">
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              Testimonianze
            </span>
            <h2 className="font-barlow text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mt-4">
              Cosa Dicono<br />
              <span className="text-gray-500">I Nostri Clienti</span>
            </h2>
          </div>

          {/* Testimonials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
            {testimonials.map((item, index) => (
              <div
                key={index}
                className="bg-revo-paper border border-revo-border p-8 relative group hover:border-revo-orange/30 transition-colors duration-300"
                data-testid={`testimonial-${index}`}
              >
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 text-revo-orange/20 font-barlow text-6xl font-black">
                  "
                </div>

                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-revo-orange text-revo-orange" />
                  ))}
                </div>

                {/* Quote */}
                <p className="font-manrope text-gray-300 leading-relaxed mb-8 relative z-10">
                  "{item.quote}"
                </p>

                {/* Author */}
                <div className="border-t border-revo-border pt-6">
                  <p className="font-barlow text-white font-bold uppercase tracking-wide">
                    {item.author}
                  </p>
                  <p className="font-manrope text-gray-500 text-sm">
                    {item.role}, {item.company}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FAQ Section */}
      <Section className="py-24 md:py-32 bg-revo-paper" id="faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="font-manrope text-sm text-revo-orange uppercase tracking-widest">
              FAQ
            </span>
            <h2 className="font-barlow text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mt-4">
              Domande Frequenti
            </h2>
          </div>

          {/* Accordion */}
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-revo-bg border border-revo-border data-[state=open]:border-revo-orange/50 transition-colors"
              >
                <AccordionTrigger 
                  className="px-6 py-5 font-barlow text-left text-lg font-medium text-white hover:text-revo-orange hover:no-underline"
                  data-testid={`faq-trigger-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent 
                  className="px-6 pb-6 font-manrope text-gray-400 leading-relaxed"
                  data-testid={`faq-content-${index}`}
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="py-24 md:py-32 bg-revo-bg relative overflow-hidden" id="cta">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_lifeline-solutions/artifacts/b1xvhigq_OIP%20%282%29.jpg')`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-revo-bg via-revo-bg/95 to-revo-bg" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
                Proteggi Chi Lavora<br />
                <span className="text-revo-orange">In Altezza</span>
              </h2>
              <p className="font-manrope text-lg text-gray-300 leading-relaxed mb-8">
                Richiedi una consulenza gratuita per valutare le esigenze di sicurezza 
                della tua copertura. I nostri tecnici ti contatteranno entro 24 ore.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contatti"
                  data-testid="cta-primary"
                  className="inline-flex items-center justify-center gap-3 bg-revo-orange text-white px-8 py-4 font-barlow font-bold uppercase tracking-widest hover:bg-revo-orange-dark transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,102,0,0.4)]"
                >
                  Richiedi Preventivo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="tel:800864621"
                  data-testid="cta-phone"
                  className="inline-flex items-center justify-center gap-3 bg-transparent border border-revo-border text-white px-8 py-4 font-barlow font-bold uppercase tracking-widest hover:border-revo-orange hover:text-revo-orange transition-all duration-300"
                >
                  800 86 46 21
                </a>
              </div>
            </div>

            {/* Features List */}
            <div className="space-y-6">
              {[
                'Sopralluogo gratuito entro 48 ore',
                'Preventivo dettagliato senza impegno',
                'Installatori certificati UNI 11900',
                'Assistenza post-vendita garantita',
                'Documentazione completa inclusa'
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 bg-revo-paper/50 backdrop-blur-sm border border-revo-border p-4"
                >
                  <CheckCircle2 className="w-6 h-6 text-revo-orange flex-shrink-0" />
                  <span className="font-manrope text-white">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default HomePage;
